import { RegisterKind, RegisterKindType, SummaryDocumentKind } from 'api/common/constants'
import { toHalfWidth } from 'utilities/moji'
import { PreRegisterFormValues } from 'utilities/resources/formItemValues'
import { regulationEscape } from 'utilities/typeUtility'

/**
 * 検証結果
 */
export enum ValidateResult {
  /** 有効 */
  Valid = 0,
  /** 無効 */
  Invalid = 1,
  /** 未入力 */
  Empty = 2,
  /** 文字数が足りない */
  LengthTooShort = 3,
  /** 未選択 */
  Unselected = 4,
  /** 20才未満 */
  AgeTooYoung = 5,
  /** 値が一致しない */
  Mismatch = 6,
}

type ErrorMessages = { [key in ValidateResult]?: string }
export type FormItemType = 'none' | 'must' | 'company' | 'subscription'
type FormItemProps = {
  label: string
  name: string
  type: FormItemType
  checks?: ValidateResult[]
  errorMessages?: ErrorMessages
}

export type FormItems<T> = { [P in keyof T]: FormItemProps }
export type FormErrorMessage<T> = { [P in keyof T]: string }

const messageTemplateMap = new Map<ValidateResult, string>([
  [ValidateResult.Invalid, '{item}が正しくありません。'],
  [ValidateResult.Empty, '{item}を入力してください。'],
  [ValidateResult.LengthTooShort, '{item}の文字数が足りません。'],
  [ValidateResult.Unselected, '{item}を選択してください。'],
  [ValidateResult.AgeTooYoung, '20才未満の方はご登録できません。'],
  [ValidateResult.Mismatch, '値が一致していません。'],
])

/**
 * エラーチェッカー
 */
export class ErrorChecker<T extends {}> {
  private errorMessageData: FormErrorMessage<T>
  private formItems: FormItems<T>
  private invalid = false
  private invalidFirstItemId: string | null = null

  constructor(formItems: FormItems<T>) {
    this.errorMessageData = {} as FormErrorMessage<T>
    this.formItems = formItems
  }

  public init() {
    this.errorMessageData = {} as FormErrorMessage<T>
    this.invalid = false
    this.invalidFirstItemId = null
  }

  /**
   * エラーチェック
   * @param result 検証結果
   * @param key 検証パラメータ
   * @param errorMessages エラーメッセージ
   */
  public check(result: ValidateResult, key: keyof T, errorMessages?: ErrorMessages) {
    const formItem = this.formItems[key]
    const checks = formItem.checks ?? []
    if (result === ValidateResult.Valid || (checks.length > 0 && !checks.includes(result))) {
      this.errorMessageData[key] = ''
    } else {
      const template = errorMessages?.[result] ?? formItem.errorMessages?.[result] ?? messageTemplateMap.get(result) ?? ''
      this.errorMessageData[key] = template.replace('{item}', formItem.label)
      this.invalid = true
      this.invalidFirstItemId = this.invalidFirstItemId ?? formItem.name
    }
  }

  /**
   * 入力チェック
   * @param formValues フォーム値オブジェクト
   * @param targetTypes 対象の項目タイプリスト
   * @returns
   */
  public isAllEntered(formValues: T, targetTypes: FormItemType[]) {
    return Object.entries(formValues).every(([key, value]) => (targetTypes.includes(this.formItems[key as keyof T].type) ? value : true))
  }

  public isValid() {
    return !this.invalid
  }

  public getErrorMessageData() {
    return this.errorMessageData
  }

  /**
   * 最初にエラーになった項目のIDを取得
   * @returns
   */
  public getFirstInvalidItemId() {
    return this.invalidFirstItemId ? `input_item_id_${this.invalidFirstItemId}` : null
  }
}

/**
 * 入力有無の検証
 * @param value
 * @returns
 */
export const emptyValidator = (value: any): ValidateResult => {
  if (value) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Empty
  }
}

/**
 * 選択の検証
 * @param value 値
 * @param items 選択オブジェクト
 * @returns
 */
export function selectValidator(value: string | number | null | undefined, items: {}): ValidateResult {
  for (const val of Object.values(items)) {
    if (val === value || val === Number(value)) {
      return ValidateResult.Valid
    }
  }
  return ValidateResult.Unselected
}

const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
/**
 * Eメールアドレスの検証
 * @param email
 * @returns
 */
export const emailValidator = (email: string | null | undefined): ValidateResult => {
  if (!email) {
    return ValidateResult.Empty
  } else if (emailRegex.test(email)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const telRegex = /^0[7-9]0\d{8}$/
/**
 * 携帯電話番号の検証
 * @param tel
 * @returns
 */
export const telValidator = (tel: string | null | undefined): ValidateResult => {
  if (!tel) {
    return ValidateResult.Empty
  } else if (telRegex.test(tel)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const dateRegex = /^\d{4}\d{2}\d{2}$/
/**
 * 日付の検証
 * @param date
 * @returns
 */
export const dateValidator = (date: string | null | undefined): ValidateResult => {
  if (!date) {
    return ValidateResult.Empty
  } else if (dateRegex.test(date)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

/**
 * 誕生日の検証
 * @param birthday
 * @returns
 */
export const birthdayValidator = (birthday: string): ValidateResult => {
  if (!birthday) {
    return ValidateResult.Empty
  } else if (!dateRegex.test(birthday)) {
    return ValidateResult.Invalid
  } else {
    const year = parseInt(birthday.substring(0, 4), 10)
    const month = parseInt(birthday.substring(4, 6), 10)
    const day = parseInt(birthday.substring(6, 8), 10)
    const birthdayDate = new Date(year, month - 1, day)

    const today = new Date()
    const thisYearBirthday = new Date(today.getFullYear(), birthdayDate.getMonth(), birthdayDate.getDate())
    let age = today.getFullYear() - year
    if (today < thisYearBirthday) {
      age--
    }
    if (age < 20) {
      return ValidateResult.AgeTooYoung
    }
    return ValidateResult.Valid
  }
}

const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[a-zA-Z0-9]{8,}$/
/**
 * パスワードの検証
 * @param password
 * @param passwordConfirm 指定があれば比較
 * @returns
 */
export const passwordValidator = (password: string, passwordConfirm?: string): ValidateResult => {
  if (!password) {
    return ValidateResult.Empty
  } else if (password.length < 8) {
    return ValidateResult.LengthTooShort
  } else if (passwordRegex.test(password)) {
    if (passwordConfirm && password !== passwordConfirm) {
      return ValidateResult.Mismatch
    } else {
      return ValidateResult.Valid
    }
  } else {
    return ValidateResult.Invalid
  }
}

/**
 * ユーザー名（カナ）の検証
 * 全角カナのみ
 */
const userNameKanaRegex = /^[ァ-ヴー]+$/
export const userNameKanaValidator = (text: string | null | undefined): ValidateResult => {
  if (!text) {
    return ValidateResult.Empty
  } else if (userNameKanaRegex.test(text)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

/**
 * ユーザー名（ローマ字）の検証
 * アルファベットのみ
 */
const userNameRomanRegex = /^[a-z]+$/i
export const userNameRomanValidator = (text: string | null | undefined): ValidateResult => {
  if (!text) {
    return ValidateResult.Empty
  } else if (userNameRomanRegex.test(text)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

/**
 * 法人名（カナ）の検証
 * 全角カナと記号のみ
 */
const companyNameKanaRegex = /^[ァ-ヴー　！”＃＄％＆’（）*+，−．／：；＜＝＞？＠［＼］＾＿｀｛｜｝〜「」 -/:-@[-`{-~]+$/
export const companyNameKanaValidator = (text: string | null | undefined): ValidateResult => {
  if (!text) {
    return ValidateResult.Empty
  } else if (companyNameKanaRegex.test(text)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const companyInvoiceNumberRegex = /^[0-9]{13}$/
/**
 * 適格事業者番号（インボイス番号）の検証
 * @param invoiceNumber
 * @returns
 */
export const companyInvoiceNumberValidator = (invoiceNumber: string): ValidateResult => {
  if (!invoiceNumber) {
    return ValidateResult.Empty
  } else if (companyInvoiceNumberRegex.test(invoiceNumber)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const postalCodeRegex = /^[[0-9]{7}$/
/**
 * 郵便番号の検証
 * @param postalCode
 * @returns
 */
export const postalCodeValidator = (postalCode: string): ValidateResult => {
  if (!postalCode) {
    return ValidateResult.Empty
  } else if (postalCodeRegex.test(postalCode)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const bankCodeRegex = /^[0-9]{4}$/
/**
 * 銀行コードの検証
 * @param bankCode
 * @returns
 */
export const bankCodeValidator = (bankCode: string): ValidateResult => {
  if (!bankCode) {
    return ValidateResult.Empty
  } else if (bankCodeRegex.test(bankCode)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const bankBranchCodeRegex = /^[0-9]{3}$/
/**
 * 銀行支店コードの検証
 * @param bankBranchCode
 * @returns
 */
export const bankBranchCodeValidator = (bankBranchCode: string): ValidateResult => {
  if (!bankBranchCode) {
    return ValidateResult.Empty
  } else if (bankBranchCodeRegex.test(bankBranchCode)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

const bankAccountNumberRegex = /^[0-9]{7}$/
/**
 * 銀行口座番号の検証
 * @param bankAccountNumber
 * @returns
 */
export const bankAccountNumberValidator = (bankAccountNumber: string): ValidateResult => {
  if (!bankAccountNumber) {
    return ValidateResult.Empty
  } else if (bankAccountNumberRegex.test(bankAccountNumber)) {
    return ValidateResult.Valid
  } else {
    return ValidateResult.Invalid
  }
}

// 銀行口座名義の置き換えキーワード
const keywords = JSON.parse(process.env.REACT_APP_BANK_ACCOUNT_NAME_REPLACE_KEYWORDS ?? '[]')
const keywordMap = new Map<string, string>(keywords)

/**
 * 銀行口座名義（半角カナ）の検証
 * 氏名（カナ）と合っているか
 * @param bankAccountName 銀行口座名義（カナ）
 * @param name 氏名（カナ）
 * @returns
 */
export const bankAccountNameValidator = (bankAccountName: string, name: string): ValidateResult => {
  if (!bankAccountName) {
    return ValidateResult.Empty
  } else {
    const trimmedAccountName = toHalfWidth(bankAccountName, 'all-trim')
    const trimmedTargetName = toHalfWidth(name, 'all-trim')
    if (trimmedAccountName === trimmedTargetName) {
      return ValidateResult.Valid
    } else {
      for (const [target, replace] of Array.from(keywordMap.entries())) {
        const targetText = toHalfWidth(target, 'all-trim')
        const targetReg = new RegExp(regulationEscape(targetText), 'g')
        const replacedAccountName = trimmedAccountName.replace(targetReg, toHalfWidth(replace, 'all-trim'))
        if (replacedAccountName === trimmedTargetName) {
          return ValidateResult.Valid
        }
      }
      return ValidateResult.Mismatch
    }
  }
}

/**
 * ATEMクエストIDの検証
 * @param atemQuestId ATEMクエストID
 * @returns
 */
export const atemQuestIdValidator = (atemQuestId: string): ValidateResult => {
  if (!atemQuestId) {
    return ValidateResult.Empty
  } else {
    return ValidateResult.Valid
  }
}

const registerKinds = Object.values(RegisterKind)
/**
 * 招待コードの検証
 * @param kind コネクター登録種別
 * @param hash 招待コードHash
 * @returns
 */
export const checkInviteCode = (kind?: string, hash?: string): boolean => Boolean(registerKinds.includes(kind as RegisterKindType) && hash)

/**
 * 仮登録情報: 概要書面を選択済みか
 * @param formValues 仮登録情報
 * @returns
 */
export const isSelectedSummaryDocument = (formValues?: PreRegisterFormValues): formValues is PreRegisterFormValues =>
  Boolean(
    formValues &&
      (formValues.summaryDocumentKind === SummaryDocumentKind.Electronic || (formValues.summaryDocumentKind === SummaryDocumentKind.Paper && formValues.summaryDocumentNumbers)),
  )
